import { useEffect, useRef } from 'react';

import { recapOutcome } from './recapOutcome';
import { recapToPlainText } from './recapText';
import {
    isTranscriptFileTooLargeError,
    isTranscriptUnsupportedFormatError,
} from './transcriptUploadRefusal';

import type {
    MeetingRecapRetry,
    MeetingTranscriptLabels,
    MeetingTranscriptLoadFailure,
    MeetingTranscriptProps,
} from './types';
import type { AiGenerationState } from '@/hooks/ai/useAiGeneration';
import type { MeetingRecap, MeetingRecapCommitment } from '@/types/review/PostReviewMeeting';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useError } from '@/hooks/utils/useError';
import { showErrorToast } from '@/plugins/Toast';
import { downloadFile } from '@/utils/fileDownload';

/**
 * The owners the server names. Anything else reads as `undetermined`, so no raw translation key
 * reaches the screen and every journey words an unattributed commitment the same way (#15411).
 */
const RECAP_OWNERS = ['manager', 'employee', 'both', 'undetermined'] as const;

const ownerKey = (owner: MeetingRecapCommitment['owner']) =>
    RECAP_OWNERS.find((known) => known === owner) ?? 'undetermined';

/** The translation namespace each journey words the transcript screen with. */
type MeetingTranscriptNamespace =
    | 'postReviewMeeting.transcript'
    | 'thematicMeetings.plan.transcript';

interface TranscriptQuery {
    data: { fileName: string | null; recap: MeetingRecap | null } | null | undefined;
    isPending: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => unknown;
}

interface TranscriptUpload {
    mutate: (
        file: File,
        options: { onSuccess: () => void; onError: (error: unknown) => void },
    ) => void;
    isPending: boolean;
}

interface RecapGeneration {
    state: AiGenerationState;
    generate: () => Promise<void>;
}

interface UseMeetingTranscriptScreenParams {
    namespace: MeetingTranscriptNamespace;
    transcriptQuery: TranscriptQuery;
    upload: TranscriptUpload;
    recapGeneration: RecapGeneration;
    /**
     * The size limit this journey enforces, said back to the manager when the server refuses a file
     * for crossing it. The assessment and the thematic meeting share the refusal code but not the
     * number (10 MB against 2 MB), so the message can never be the same generic sentence for both.
     */
    maxFileSizeMb: number;
    disabled?: boolean;
}

/** What every journey hands the screen the same way; the rest is the journey's own. */
type MeetingTranscriptScreenProps = Omit<
    MeetingTranscriptProps,
    'onDownloadTranscript' | 'acceptedFileTypes' | 'spacing' | 'dataTestId'
>;

/**
 * Only an action the manager just took reports its failure as an error. A generation found running on
 * arrival says nothing alarming, and neither does a wait that ran out: the recap may still be written,
 * and the screen already says it is unavailable for now, with a way to try again.
 */
const isReportedFailure = (state: AiGenerationState) =>
    state.status === 'failed' && state.origin === 'user' && state.reason === 'error';

/**
 * The behaviour of the transcript screen that does not depend on the journey: reading the transcript,
 * importing one and then asking for its recap, waiting on the recap, offering to try again, reporting
 * what failed. The journey brings its own queries, its recap generation and its wording.
 */
export const useMeetingTranscriptScreen = ({
    namespace,
    transcriptQuery,
    upload,
    recapGeneration,
    maxFileSizeMb,
    disabled = false,
}: UseMeetingTranscriptScreenParams): MeetingTranscriptScreenProps => {
    const { t } = useAmelioTranslations();
    const { getManagerLabelCapitalized } = useCompanyInformation();
    const { handleError } = useError();
    // Read by the failure effect rather than listed in it: `t` may be a new function on every render,
    // which would report the same failure again each time.
    const translate = useRef(t);
    useEffect(() => {
        translate.current = t;
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const transcript = transcriptQuery.data;
    const recap = transcript?.recap ?? null;
    const { state: recapState, generate: generateRecap } = recapGeneration;

    useEffect(() => {
        if (isReportedFailure(recapState)) {
            showErrorToast({ message: translate.current(`${namespace}.recapError`) });
        }
    }, [recapState, namespace]);

    const handleChooseFile = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = disabled ? undefined : event.target.files?.[0];
        // Refused here rather than left to the server: an empty file has nothing to read, and the
        // manager is told so at the moment they picked it.
        if (file?.size === 0) {
            showErrorToast({ message: t('errors.transcriptEmptyFile') });
        } else if (file) {
            // Asked for once the file is stored: a recap of a transcript the server has not accepted
            // would describe the previous one, or nothing at all.
            upload.mutate(file, {
                onSuccess: () => void generateRecap(),
                // The server names what it refused (empty file, unreadable content) and useError words it. The size
                // cap and the accepted formats are worded here: they differ between journeys, and the shared
                // dictionary could only state one of them.
                onError: (error) => {
                    if (isTranscriptFileTooLargeError(error)) {
                        showErrorToast({
                            message: t(`${namespace}.uploadFileTooLarge`, { maxFileSizeMb }),
                        });
                    } else if (isTranscriptUnsupportedFormatError(error)) {
                        showErrorToast({ message: t(`${namespace}.uploadUnsupportedFormat`) });
                    } else {
                        handleError({ error, data: { message: t(`${namespace}.uploadError`) } });
                    }
                },
            });
        }
        // Cleared every time: picking the same file twice fires no change event otherwise, so a failed
        // upload could not be retried with the same file.
        event.target.value = '';
    };

    const labels: MeetingTranscriptLabels = {
        title: t(`${namespace}.title`),
        description: t(`${namespace}.description`),
        uploadPrompt: t(`${namespace}.uploadPrompt`),
        chooseFile: t(`${namespace}.chooseFile`),
        recapTitle: t(`${namespace}.recapTitle`),
        generatedBy: t(`${namespace}.generatedBy`),
        importAnother: t(`${namespace}.importAnother`),
        downloadRecap: t(`${namespace}.downloadRecap`),
        discussedPoints: t(`${namespace}.discussedPoints`),
        decisions: t(`${namespace}.decisions`),
        commitments: t(`${namespace}.commitments`),
        followUps: t(`${namespace}.followUps`),
        commitmentOwner: (owner) =>
            t(`${namespace}.owner.${ownerKey(owner)}`, {
                managerLabel: getManagerLabelCapitalized(),
            }),
        disclosure: t(`${namespace}.disclosure`),
        unusableTitle: t(`${namespace}.unusableTitle`),
        unusableBody: t(`${namespace}.unusableBody`),
    };

    // A transcript already read stays on screen when a later read fails.
    const transcriptLoadFailure: MeetingTranscriptLoadFailure | undefined =
        transcriptQuery.isError && !transcript
            ? {
                  message: t(`${namespace}.loadError`),
                  retryLabel: t(`${namespace}.retry`),
                  onRetry: () => void transcriptQuery.refetch(),
                  isRetrying: transcriptQuery.isFetching,
              }
            : undefined;

    const recapRetry: MeetingRecapRetry = {
        onRetry: () => void generateRecap(),
        pendingMessage: t(`${namespace}.recapPending`),
        unavailableMessage: t(`${namespace}.recapUnavailable`),
        retryLabel: t(`${namespace}.retry`),
    };

    // Written from the labels the panel shows, so the file and the screen never say two different things.
    // Nothing is offered for an unusable recap: the file would be blank, and a blank file imported back as a
    // transcript is what bug 16674 was.
    const handleDownloadRecap =
        recap && recapOutcome(recap) !== 'unusable'
            ? () =>
                  downloadFile(
                      new Blob([recapToPlainText(recap, labels)], {
                          type: 'text/plain;charset=utf-8',
                      }),
                      'meeting-recap.txt',
                  )
            : undefined;

    return {
        labels,
        isLoading: transcriptQuery.isPending,
        transcriptLoadFailure,
        hasTranscript: Boolean(transcript?.fileName),
        recap,
        isBusy: upload.isPending || recapState.status === 'pending',
        fileInputRef,
        onChooseFile: handleChooseFile,
        onFileSelected: handleFileSelected,
        recapRetry,
        onDownloadRecap: handleDownloadRecap,
        disabled,
    };
};
